import type { ExplorerColumn, ExplorerRow } from 'features/Explorer/types/explorer.types'

import { asArray, asNumber, asText } from '../payload'

/**
 * The Explorer table's payload (MYD-504), as explorerElements.js returns it,
 * and the paging both the renderer and the presenter's continuation slides
 * read from it.
 *
 * Read defensively: a payload is server data, and a deck can outlive the shape
 * it was saved with.
 */

/** Mirrors MAX_PAGES in explorerElements.js: the most slides one table runs to. */
export const MAX_PAGES = 10

export interface ExplorerTablePayload {
	report: { id: string; name: string } | null
	grain: string
	rowLabel: string
	columns: ExplorerColumn[]
	groupBy: { key: string; label: string } | null
	rows: ExplorerRow[]
	total: number
	rowLimit: number
	overflow: 'truncate' | 'continue'
	filters: string[]
	hiddenColumns: number
	/** Set by the presenter on a continuation slide: which page this one shows, 0-based. */
	page?: number
	/** Set instead of everything else when nothing has been chosen yet. */
	empty?: string
}

const isColumn = (value: unknown): value is ExplorerColumn =>
	Boolean(value) && typeof (value as ExplorerColumn).key === 'string' && typeof (value as ExplorerColumn).label === 'string'

/** The payload with every field present and of the right kind, or null when it is not a table at all. */
export const readExplorerTable = (payload: unknown): ExplorerTablePayload | null => {
	const source = (payload && typeof payload === 'object' ? payload : {}) as Record<string, any>
	if (asText(source.empty)) return { ...EMPTY, empty: asText(source.empty) }

	const columns = asArray<unknown>(source.columns).filter(isColumn)
	if (columns.length === 0) return null

	const rowLimit = Math.max(1, Math.floor(asNumber(source.rowLimit) ?? 12))
	const rows = asArray<ExplorerRow>(source.rows).filter(row => row && typeof row === 'object')
	return {
		report: source.report && asText(source.report.id) ? { id: asText(source.report.id), name: asText(source.report.name) } : null,
		grain: asText(source.grain),
		rowLabel: asText(source.rowLabel) || 'row',
		columns,
		groupBy: source.groupBy && asText(source.groupBy.key) ? { key: asText(source.groupBy.key), label: asText(source.groupBy.label) } : null,
		rows,
		total: Math.max(asNumber(source.total) ?? rows.length, rows.length),
		rowLimit,
		overflow: source.overflow === 'continue' ? 'continue' : 'truncate',
		filters: asArray<unknown>(source.filters).map(asText).filter(Boolean),
		hiddenColumns: Math.max(0, Math.floor(asNumber(source.hiddenColumns) ?? 0)),
		page: Math.max(0, Math.floor(asNumber(source.page) ?? 0)),
	}
}

const EMPTY: ExplorerTablePayload = {
	report: null,
	grain: '',
	rowLabel: 'row',
	columns: [],
	groupBy: null,
	rows: [],
	total: 0,
	rowLimit: 12,
	overflow: 'truncate',
	filters: [],
	hiddenColumns: 0,
}

/** One line of a page: a group's heading, or a data row with its position in the fetched rows. */
export type TableLine = { kind: 'group'; value: unknown } | { kind: 'row'; row: ExplorerRow; index: number }

/**
 * The table laid out into pages of `rowLimit` LINES, group headings included,
 * so a page never holds more than the slide has room for. A group that runs
 * onto a new page repeats its heading there, and a heading is never the last
 * line of a page.
 */
export const layoutPages = (table: ExplorerTablePayload): TableLine[][] => {
	const key = table.groupBy?.key ?? null
	const pages: TableLine[][] = []
	let page: TableLine[] = []
	let group: unknown = Symbol('none')

	table.rows.forEach((row, index) => {
		const startsGroup = key !== null && (index === 0 || row[key] !== group)
		// A heading and its first row must fit together; a continued group needs its heading again.
		const needed = key !== null && (startsGroup || page.length === 0) ? 2 : 1
		if (page.length > 0 && page.length + needed > table.rowLimit) {
			pages.push(page)
			page = []
		}
		if (key !== null && (startsGroup || page.length === 0)) page.push({ kind: 'group', value: row[key] })
		page.push({ kind: 'row', row, index })
		if (key !== null) group = row[key]
	})
	if (page.length > 0) pages.push(page)
	return pages
}

/**
 * How many slides the table fills: one, unless it continues and has the rows
 * for more. Only the rows actually fetched are paged; past MAX_PAGES the last
 * slide's footer says how many more there are.
 */
export const pageCount = (table: ExplorerTablePayload | null): number => {
	if (!table || table.empty || table.overflow !== 'continue') return 1
	return Math.min(MAX_PAGES, Math.max(1, layoutPages(table).length))
}

/**
 * Row height, by how many lines a page may hold. A slide is 720 high; the
 * densest step still fits MAX_ROW_LIMIT lines with the heading and footer.
 */
export const densityFor = (rowLimit: number): 'regular' | 'compact' | 'dense' => (rowLimit <= 10 ? 'regular' : rowLimit <= 14 ? 'compact' : 'dense')
